import axios from 'axios';
import { SendToCCPayload } from '../components/LMA/types';
import { post } from 'lib/utils/axiosFetcher';

export interface ReferralDataResponse {
  data: any[];
  totalRecords: number;
  success: boolean;
  message?: string;
}

export interface FilterParams {
  [key: string]: string;
}

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface FetchReferralDataPayload {
  leadStatusParams?: Record<string, string>;
  filters?: FilterParams | null;
  pagination?: PaginationParams;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  configuration?: {
    leads: Record<string, string>;
  };
}

const objectToQueryString = (obj: any) => {
  const keys = Object.keys(obj);
  const keyValuePairs = keys.map((key) => {
    return encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]);
  });
  return keyValuePairs.join('&');
};

/**
 * Fetch referral data with POST method supporting filters and pagination
 * This API now handles server-side filtering, pagination, and sorting
 * leadType and leadStatusParams are sent as URL query params
 * Filters, Page, and PageSize are sent in POST body
 */
export const fetchReferralData = async (
  params: any,
  payload: FetchReferralDataPayload
): Promise<ReferralDataResponse> => {
  try {
    const url = `/LMA/GetReferralData?${objectToQueryString(params)}`;

    // Clean filters - remove empty string values
    const cleanedFilters = Object.entries(payload.filters || {})
      .filter(([, value]) => value !== '' && value != null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    payload.filters = Object.keys(cleanedFilters).length === 0 ? null : cleanedFilters;

    // POST body contains Filters, Page, PageSize, and optional sorting parameters
    const response = await post(url, payload);

    return response.data;
  } catch (error) {
    console.error('Error fetching referral data:', error);
    throw error;
  }
};

export const fetchProcessedReferralData = async (
  params: any,
  payload: FetchReferralDataPayload
): Promise<any> => {
  try {
    const url = `/LMA/GetProcessedData?${objectToQueryString(params)}`;

    // Clean filters - remove empty string values
    const cleanedFilters = Object.entries(payload.filters || {})
      .filter(([, value]) => value !== '' && value != null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    payload.filters = Object.keys(cleanedFilters).length === 0 ? null : cleanedFilters;

    const response = await post(url, payload);

    return response.data;
  } catch (error) {
    throw error;
  }
};

//Calls the backend API to add an LMA user.
export const addLMAUser = async (userName: string): Promise<any> => {
  try {
    const response = await axios.post('/api/dap-api', {
      url: '/LMA/AddLMAUser',
      data: { userName }, // wrap in object for backend compatibility
    });
    return response?.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Sends a patient record to Clinical Conductor (CC)
 *
 * @param payload - The patient data to send
 * @returns Promise with the API response
 * @throws Error if the API call fails
 */

export const sendToCC = async (payload: SendToCCPayload) => {
  try {
    const response = await axios.post('/api/dap-api', {
      url: '/LMA/SendToCC',
      data: payload,
    });
    return response.data;
  } catch (error: any) {
    console.error('[sendToCC] Error message:', error?.message);
    throw error;
  }
};

export interface SyncedResource {
  conditionId?: number;
  procedureId?: number;
  id?: number;
  immunizationId?: number;
  allergyId?: number;
  familyMemberId?: number;
  deviceId?: number;
  value: string;
  codingSystem: string;
  synced: boolean;
}

export interface GetSyncedResourcesResponse {
  conditions?: SyncedResource[];
  procedures?: SyncedResource[];
  medications?: SyncedResource[];
  immunizations?: SyncedResource[];
  allergies?: SyncedResource[];
  familyMemberHistory?: SyncedResource[];
  devices?: SyncedResource[];
}

export interface GetSyncedResourcesParams {
  guestRef: string;
  enrollmentId: string;
  leadId: number;
  leadStatus: 'Requested' | 'Processed';
}

/**
 * Get synced resources from CDP
 * Returns sync status for conditions, procedures, medications, immunizations, allergies, family history, and devices
 *
 * @param params - Query parameters (guestRef, enrollmentId, leadId, leadStatus)
 * @returns Promise with synced resources data
 */
export const getSyncedResources = async (
  params: GetSyncedResourcesParams
): Promise<GetSyncedResourcesResponse> => {
  try {
    const queryString = objectToQueryString(params);
    const url = `/LMA/GetSyncedResources&${queryString}`;

    const response = await axios.get('/api/dap-api?url=' + url);

    return response.data;
  } catch (error: any) {
    console.error('[getSyncedResources] Error:', error?.message);
    throw error;
  }
};

export interface SyncResourcesPayload {
  ccId: string;
  leadId: number;
  userId: string;
  guestRef: string;
  enrollmentId: string;
  allResourcesSelected: boolean;
  resources: {
    conditions?: Array<{
      conditionId: number;
      startDate?: string | null;
      endDate?: string | null;
      ageAtOnset?: number | null;
      comment?: string | null;
    }> | null;
    procedures?: Array<{
      procedureId: number;
      comment?: string | null;
      cptCode?: string | null;
      procedureBy?: string | null;
      date?: string | null;
    }> | null;
    medications?: Array<{
      id: number;
      dosage?: string | null;
      startDate?: string | null;
      endDate?: string | null;
      comment?: string | null;
      routeId?: number | null;
    }> | null;
    immunizations?: Array<{
      immunizationId: number;
      comment?: string | null;
      location?: string | null;
      date?: string | null;
    }> | null;
    allergies?: Array<{
      allergyId: number;
      reaction?: string | null;
      comment?: string | null;
      startDate?: string | null;
      endDate?: string | null;
    }> | null;
    devices?: Array<{
      deviceId: number;
      comment?: string | null;
    }> | null;
    familyMemberHistory?: null;
  };
}

/**
 * Sync selected resources to CDP
 * Sends unsynced resources from the Processed tab to Clinical Conductor
 *
 * @param payload - The sync payload with selected resources
 * @returns Promise with the API response
 */
export const syncResources = async (payload: SyncResourcesPayload): Promise<any> => {
  try {
    const response = await axios.post('/api/dap-api', {
      url: '/LMA/SyncResources',
      data: payload,
    });

    return response.data;
  } catch (error: any) {
    console.error('[syncResources] Error:', error?.message);
    throw error;
  }
};
